import { useState } from "react";
import { BookOpen, ChevronDown, ChevronRight, Search, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const sections = [
  {
    id: "getting-started",
    title: "Getting Started",
    content: [
      { q: "What is Red Ink Studios?", a: "A premium audio drama, book, and music platform hosting three series: The Red Ink Chronicles (TRIC), The Notary's Shadow (TNS), and Signed, Sealed, Betrayed (SSB). Over 1,200 episodes across all three." },
      { q: "How do I create an account?", a: "Click 'Sign In' in the top-right corner. Create a free account with your email and a password — that's it. You'll be reading and listening in seconds." },
      { q: "What are Credits?", a: "Credits are the currency for unlocking episodes. You get 1 free episode per 24 hours. Buy more in the Store, or earn them by submitting stories to the Witness Stand." },
    ]
  },
  {
    id: "episodes",
    title: "Reading Episodes",
    content: [
      { q: "How do I browse episodes?", a: "Navigate to EPISODES. Use the series selector at the top to switch between TRIC, TNS, and SSB. Season tabs (S1, S2...) filter by season." },
      { q: "Why is an episode locked?", a: "Locked episodes require credits. You get 1 free episode per day, or you can purchase credit packages in the Store." },
      { q: "Can I listen to episodes?", a: "Episodes with TTS audio have a built-in audio player. Not all episodes have audio generated yet." },
    ]
  },
  {
    id: "store",
    title: "Credit Store",
    content: [
      { q: "How do I buy credits?", a: "Go to STORE, choose a package (5 to 100 credits), click Buy, and complete checkout through Stripe. Credits are added instantly." },
      { q: "Do credits expire?", a: "No. Credits stay in your account until you spend them." },
      { q: "What's the best value?", a: "The 100 credit package ($25) gives you 110 total credits (+10 bonus) — best per-credit price." },
    ]
  },
  {
    id: "series-bible",
    title: "Series Bible",
    content: [
      { q: "What is the Series Bible?", a: "A character gallery showing all characters across the three series with AI-generated ethnic-styled portraits, descriptions, and roles." },
      { q: "Can I switch between Gallery and List view?", a: "Yes! Use the Gallery/List toggle buttons at the top of the character display." },
    ]
  },
  {
    id: "witness-stand",
    title: "Witness Stand",
    content: [
      { q: "What is the Witness Stand?", a: "A place for readers to submit their own notary stories. If your story inspires a future episode, you earn 10 Credits." },
      { q: "Do I need an account to submit?", a: "Yes, you must be signed in to submit a case file." },
    ]
  },
  {
    id: "production",
    title: "Production Hub (Admin)",
    content: [
      { q: "How do I access Production?", a: "Click PRODUCTION in the nav bar, then enter the admin password. This is an admin-only area." },
      { q: "What's the Overview tab?", a: "Shows production pipeline stats: stories expanded, thumbnails, audio ready, and completion percentages for Book/eBook, Audiobook, and Video Book pipelines." },
      { q: "What's the Story Input tab?", a: "Where you expand episode content using AI. Select a season and click 'Auto Expand Season' — it runs in the background so you can navigate away freely." },
      { q: "Can I leave the page during Auto Expand?", a: "YES! The expansion runs on the server. Navigate anywhere and come back — your progress continues. Check episode list for green dots to confirm completion." },
      { q: "What's the Assets tab?", a: "Manages thumbnails, music albums, and includes the Story Revision Scanner that checks for quality issues across episodes." },
      { q: "How does the Story Revision Scanner work?", a: "Click 'Scan Season X' to analyze all episodes for: missing stories, short summaries, missing thumbnails/loglines, AI self-reference leaks, and formatting issues." },
      { q: "What's the Backup button?", a: "Downloads a complete JSON backup of the selected series including all episodes, characters, lore, and voice profiles. Use this before any risky operations like Force Seed." },
    ]
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    content: [
      { q: "Production login keeps spinning", a: "Wait 5-10 seconds. If still spinning, refresh and try again. Password is case-sensitive: RedInk2026!" },
      { q: "Auto Expand seems to do nothing", a: "The background job starts but takes ~10-15 seconds per batch of 5 episodes. Stay on the tab briefly to see the progress banner appear. For 50 episodes, expect ~2 minutes." },
      { q: "Character portraits show broken images", a: "External image URLs may have expired. Go to Character Editor, click 'Generate Preview' to create a new portrait, then Save." },
      { q: "Lost data after Force Seed", a: "If you have a backup JSON, restore via POST /api/backup/restore. Otherwise, use Auto Expand to regenerate summaries." },
      { q: "'AI returned invalid format' error", a: "Transient error — the AI occasionally returns malformed output. Simply click the button again." },
      { q: "Season progress shows 0% after expanding", a: "The tracker counts summaries > 150 characters as 'expanded'. Run Auto Expand to generate rich summaries." },
      { q: "Will my data survive deployment?", a: "Yes! The seed script only runs when the database is empty. Your content persists across deployments. Only Force Seed (/api/seed/force) overwrites data." },
      { q: "How to check system health?", a: "Visit /diag to see database status, document counts, and environment info." },
    ]
  },
];

export default function HelpPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [openSections, setOpenSections] = useState(new Set(["getting-started"]));

  const toggleSection = (id) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const filtered = sections.map(s => ({
    ...s,
    content: s.content.filter(item =>
      !search || item.q.toLowerCase().includes(search.toLowerCase()) || item.a.toLowerCase().includes(search.toLowerCase())
    )
  })).filter(s => s.content.length > 0);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="help-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <button onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-gray-500 hover:text-white text-sm mb-6 transition-colors">
          <ArrowLeft size={14} /> Back
        </button>

        <div className="flex items-center gap-3 mb-2">
          <BookOpen size={24} className="text-red-500" />
          <h1 className="font-cinzel text-3xl font-bold">Help & User Guide</h1>
        </div>
        <p className="text-gray-500 mb-8">Everything you need to know about Red Ink Studios</p>

        {/* Search */}
        <div className="relative mb-8">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search help topics..."
            className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-gray-600 focus:border-red-500/50 outline-none"
            data-testid="help-search"
          />
        </div>

        {/* Sections */}
        <div className="space-y-3">
          {filtered.map(section => (
            <div key={section.id} className="border border-white/10 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/5 transition-colors text-left"
                data-testid={`help-section-${section.id}`}
              >
                <span className="font-cinzel font-bold text-white">{section.title}</span>
                {openSections.has(section.id) ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronRight size={16} className="text-gray-500" />}
              </button>

              {openSections.has(section.id) && (
                <div className="border-t border-white/5">
                  {section.content.map((item, i) => (
                    <div key={i} className="px-5 py-4 border-b border-white/5 last:border-b-0">
                      <h3 className="text-white text-sm font-bold mb-1.5">{item.q}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{item.a}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Download link */}
        <div className="mt-12 p-5 rounded-xl bg-white/[0.02] border border-white/10 text-center">
          <p className="text-gray-400 text-sm mb-2">Need the full manual?</p>
          <a href="/USER_MANUAL.md" target="_blank" rel="noopener noreferrer"
            className="text-red-500 hover:text-red-400 text-sm font-bold transition-colors">
            Download Complete User Manual (Markdown)
          </a>
        </div>
      </div>
    </div>
  );
}
